import { useQuery } from '@tanstack/react-query';
import { keys } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { formatBytes, stageName } from '../lib/format.ts';
import { ErrorLine } from './ui.tsx';

const count = (value: number) => value.toLocaleString('en-US');

function waited(seconds: number): string {
  if (seconds < 90) return `${seconds} s`;
  if (seconds < 90 * 60) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)} h`;
}

/**
 * How the workspace is doing, for its owners: storage and today's paid use
 * against their allowances, and background work that waits, failed or is
 * still deleting. Counts only; it refreshes as the workspace changes.
 */
export function WorkspaceStatusSection({ ws }: { ws: string }) {
  const api = useApi();
  const status = useQuery({ queryKey: keys.status(ws), queryFn: () => api.workspaceStatus(ws) });
  const data = status.data;
  return (
    <section className="section" aria-labelledby="status-heading">
      <h2 className="section-heading" id="status-heading">
        Workspace status
      </h2>
      <ErrorLine error={status.error} />
      {!data ? (
        <p className="meta">{status.isLoading ? 'Loading…' : ''}</p>
      ) : (
        <dl className="status-list">
          <div>
            <dt>Storage</dt>
            <dd>
              {formatBytes(data.storage.usedBytes)} of {formatBytes(data.storage.allowanceBytes)} used.{' '}
              <span className="meta">Files in Trash count until they are deleted for good, and so do uploads in progress.</span>
            </dd>
          </div>
          <div>
            <dt>Today (UTC)</dt>
            <dd>
              AI: {count(data.usageToday.aiRuns.used)} of {count(data.usageToday.aiRuns.allowance)} runs · Transcription: {count(data.usageToday.transcriptionMinutes.used)} of{' '}
              {count(data.usageToday.transcriptionMinutes.allowance)} minutes · Reading aloud: {count(data.usageToday.readAloudCharacters.used)} of{' '}
              {count(data.usageToday.readAloudCharacters.allowance)} characters
            </dd>
          </div>
          <div>
            <dt>Background work</dt>
            <dd>
              {data.work.waiting === 0 && data.work.running === 0
                ? 'Nothing waiting.'
                : `${count(data.work.waiting)} waiting${data.work.oldestWaitingSeconds !== null ? ` (the oldest for ${waited(data.work.oldestWaitingSeconds)})` : ''}, ${count(data.work.running)} running.`}
            </dd>
          </div>
          <div>
            <dt>Failed in the last day</dt>
            <dd>
              {data.work.failedLastDay.length ? (
                <>
                  <span className="state">{data.work.failedLastDay.map((entry) => `${stageName(entry)}: ${count(entry.count)}`).join(' · ')}.</span>{' '}
                  <span className="meta">Each item shows why, with Retry where it can be tried again.</span>
                </>
              ) : (
                'Nothing failed.'
              )}
            </dd>
          </div>
          <div>
            <dt>Deleting for good</dt>
            <dd>{data.work.deletionsInProgress ? `${count(data.work.deletionsInProgress)} in progress.` : 'Nothing in progress.'}</dd>
          </div>
          <div>
            <dt>Search by meaning</dt>
            <dd>{data.work.waitingForSearch ? `${count(data.work.waitingForSearch)} items waiting to be indexed.` : 'Up to date.'}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
